class ProductBundle extends HTMLElement {
  constructor() {
    super();
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemKeydown = this.onItemKeydown.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onSizeClick = this.onSizeClick.bind(this);
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
    this.productInfo = this.closest('product-info');

    this.sizesInput = this.querySelector('[data-sd-bundle-sizes-input]');

    this.cacheStaticPrices();

    this.items.forEach((item) => {
      item.addEventListener('click', this.onItemClick);
      item.addEventListener('keydown', this.onItemKeydown);
      item.querySelector('[data-sd-bundle-sizes]')?.addEventListener('click', this.onSizeClick);
    });

    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    this.syncSelectedItemFromForm();
  }

  cacheStaticPrices() {
    this.querySelectorAll('[data-sd-bundle-price], [data-sd-bundle-compare-price], [data-sd-include-price]').forEach(
      (element) => {
        element.dataset.sdStaticValue = element.textContent.trim();
      }
    );
  }

  restoreStaticPrices() {
    this.querySelectorAll('[data-sd-bundle-price], [data-sd-bundle-compare-price], [data-sd-include-price]').forEach(
      (element) => {
        if (element.dataset.sdStaticValue) {
          element.textContent = element.dataset.sdStaticValue;
        }
      }
    );
  }

  disconnectedCallback() {
    this.items.forEach((item) => {
      item.removeEventListener('click', this.onItemClick);
      item.removeEventListener('keydown', this.onItemKeydown);
      item.querySelector('[data-sd-bundle-sizes]')?.removeEventListener('click', this.onSizeClick);
    });

    this.variantChangeUnsubscriber?.();
  }

  onItemClick(event) {
    this.selectItem(event.currentTarget);
  }

  onItemKeydown(event) {
    if (event.target.closest('[data-sd-bundle-sizes]')) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.selectItem(event.currentTarget);
  }

  onVariantChange({ data }) {
    if (!data?.variant?.id || String(data.sectionId) !== String(this.sectionId)) {
      return;
    }

    this.restoreStaticPrices();
    this.setSelectedItemByVariantId(String(data.variant.id), false);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  getCurrentVariantId() {
    const mainFormInput = this.productInfo?.querySelector(
      `#product-form-${this.sectionId} input[name="id"]`
    );

    if (mainFormInput?.value) {
      return mainFormInput.value;
    }

    return this.productInfo?.querySelector('product-form input[name="id"]')?.value;
  }

  getDefaultItem() {
    return this.items.find((item) => item.dataset.bundleIndex === '2') || this.items[0];
  }

  hasUrlVariant() {
    return Boolean(new URLSearchParams(window.location.search).get('variant'));
  }

  syncSelectedItemFromForm() {
    const currentVariantId = this.getCurrentVariantId();

    if (this.hasUrlVariant() && currentVariantId && this.items.some((item) => item.dataset.variantId === currentVariantId)) {
      this.setSelectedItemByVariantId(currentVariantId, false);
      return;
    }

    const defaultItem = this.getDefaultItem();
    if (!defaultItem) {
      return;
    }

    const shouldUpdateProductInfo = currentVariantId !== defaultItem.dataset.variantId;
    this.setSelectedItemByVariantId(defaultItem.dataset.variantId, shouldUpdateProductInfo);
  }

  selectItem(item) {
    if (item.classList.contains('is-selected')) {
      return;
    }

    this.setSelectedItem(item, true);
  }

  findItemByVariantId(variantId) {
    const directMatch = this.items.find((item) => item.dataset.variantId === String(variantId));
    if (directMatch) return directMatch;

    const variant = this.productInfo?.getVariantById?.(variantId);
    if (!variant || !this.productInfo?.getVariantOptionValues) return null;

    const values = this.productInfo.getVariantOptionValues(variant);
    return this.items.find((item) => {
      const offerIndex = Number(item.dataset.offerOptionIndex);
      return String(values[offerIndex] || '') === String(item.dataset.offerValue || '');
    });
  }

  getOfferVariantId(item) {
    const offerValue = item.dataset.offerValue;
    if (offerValue != null && this.productInfo?.findVariantForOptionChange) {
      const variant = this.productInfo.findVariantForOptionChange(
        Number(item.dataset.offerOptionIndex),
        offerValue
      );
      if (variant?.id) return String(variant.id);
    }

    return item.dataset.variantId;
  }

  setSelectedItemByVariantId(variantId, updateProductInfo = true) {
    const selectedItem = this.findItemByVariantId(variantId);
    if (!selectedItem) return;
    this.setSelectedItem(selectedItem, updateProductInfo);
  }

  setSelectedItem(selectedItem, updateProductInfo = true) {
    this.items.forEach((bundleItem) => {
      const isSelected = bundleItem === selectedItem;
      bundleItem.classList.toggle('is-selected', isSelected);
      bundleItem.classList.toggle('is-expanded', isSelected);
      bundleItem.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
    });

    this.updateSizesInput(selectedItem);

    const variantId = updateProductInfo ? this.getOfferVariantId(selectedItem) : selectedItem.dataset.variantId;

    if (updateProductInfo && this.productInfo?.setActiveVariant) {
      this.productInfo.setActiveVariant(variantId);
    }

    const variant = this.productInfo?.getVariantById?.(variantId);
    if (variant) {
      this.productInfo?.updateSubmitButtonPrice?.(variant);
    }

    this.notifyOfferChange(selectedItem, variantId);
  }

  onSizeClick(event) {
    const button = event.target.closest('[data-sd-bundle-size]');
    if (!button) return;

    event.stopPropagation();
    if (button.classList.contains('is-selected')) return;

    button.parentElement.querySelectorAll('[data-sd-bundle-size]').forEach((option) => {
      const isSelected = option === button;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.updateSizesInput(button.closest('[data-bundle-item]'));
  }

  updateSizesInput(selectedItem) {
    if (!this.sizesInput) return;

    const container = selectedItem?.querySelector('[data-sd-bundle-sizes]');
    if (!container) {
      this.sizesInput.value = '';
      this.sizesInput.disabled = true;
      return;
    }

    const order = [];
    const counts = new Map();

    container.querySelectorAll('[data-sd-bundle-size].is-selected').forEach((button) => {
      const size = button.dataset.sdBundleSizeValue;
      if (!counts.has(size)) order.push(size);
      counts.set(size, (counts.get(size) || 0) + 1);
    });

    this.sizesInput.value = order.map((size) => `${counts.get(size)}× ${size}`).join(', ');
    this.sizesInput.disabled = this.sizesInput.value === '';
  }

  notifyOfferChange(selectedItem, variantId) {
    const priceEl = selectedItem.querySelector('[data-sd-bundle-price], [data-sd-bundle-v2-price]');

    document.dispatchEvent(
      new CustomEvent('sd:bundle-offer-change', {
        bubbles: true,
        detail: {
          variantId: String(variantId),
          price: priceEl?.dataset.sdStaticValue || priceEl?.textContent.trim() || '',
          available: selectedItem.dataset.variantAvailable !== 'false',
        },
      })
    );
  }
}

if (!customElements.get('sd-product-bundle')) {
  customElements.define('sd-product-bundle', ProductBundle);
}
